// src/client.ts
var client = null;
function configureQueries(api2) {
  client = api2;
}
function api() {
  if (!client) throw new Error("Call configureQueries(api) before using @chipn/queries hooks.");
  return client;
}

// src/contributions.ts
import { TERMINAL_PAYMENT_STATUSES } from "@chipn/schemas";
import { useInfiniteQuery as useInfiniteQuery2, useMutation as useMutation3, useQuery as useQuery3, useQueryClient as useQueryClient3 } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { unwrap as unwrap3 } from "@chipn/api-client";

// src/goals.ts
import { useMutation as useMutation2, useQuery as useQuery2, useQueryClient as useQueryClient2 } from "@tanstack/react-query";
import { unwrap as unwrap2 } from "@chipn/api-client";

// src/groups.ts
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { unwrap } from "@chipn/api-client";
var groupKeys = {
  all: ["groups"],
  list: () => [...groupKeys.all, "list"],
  detail: (id) => [...groupKeys.all, id],
  members: (id) => [...groupKeys.all, id, "members"],
  invitations: (id) => [...groupKeys.all, id, "invitations"],
  activity: (id) => [...groupKeys.all, id, "activity"]
};
function useMyGroups() {
  return useInfiniteQuery({
    queryKey: groupKeys.list(),
    initialPageParam: void 0,
    queryFn: ({ pageParam }) => unwrap(api().GET("/v1/groups", { params: { query: { limit: 20, cursor: pageParam } } })),
    getNextPageParam: (last) => last.nextCursor ?? void 0
  });
}
function useGroup(groupId) {
  return useQuery({
    queryKey: groupKeys.detail(groupId),
    queryFn: () => unwrap(api().GET("/v1/groups/{groupId}", { params: { path: { groupId } } }))
  });
}
function useMembers(groupId) {
  return useQuery({
    queryKey: groupKeys.members(groupId),
    queryFn: () => unwrap(api().GET("/v1/groups/{groupId}/members", { params: { path: { groupId } } }))
  });
}
function useInvitations(groupId, enabled) {
  return useQuery({
    queryKey: groupKeys.invitations(groupId),
    enabled,
    queryFn: () => unwrap(api().GET("/v1/groups/{groupId}/invitations", { params: { path: { groupId } } }))
  });
}
function useActivity(groupId) {
  return useQuery({
    queryKey: groupKeys.activity(groupId),
    queryFn: () => unwrap(api().GET("/v1/groups/{groupId}/activity", { params: { path: { groupId } } }))
  });
}
function useCreateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => unwrap(api().POST("/v1/groups", { body })),
    // Seed the detail cache so the next screen renders without waiting on the network.
    onSuccess: (group) => {
      qc.setQueryData(groupKeys.detail(group.id), group);
      void qc.invalidateQueries({ queryKey: groupKeys.list() });
    }
  });
}
function useUpdateGroup(groupId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => unwrap(api().PATCH("/v1/groups/{groupId}", { params: { path: { groupId } }, body })),
    onSuccess: (group) => {
      qc.setQueryData(groupKeys.detail(groupId), group);
      void qc.invalidateQueries({ queryKey: groupKeys.list() });
    }
  });
}
function useArchiveGroup(groupId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api().POST("/v1/groups/{groupId}/archive", { params: { path: { groupId } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: groupKeys.all })
  });
}
function useChangeRole(groupId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }) => unwrap(
      api().PATCH("/v1/groups/{groupId}/members/{userId}", {
        params: { path: { groupId, userId } },
        body: { role }
      })
    ),
    onSuccess: () => qc.invalidateQueries({ queryKey: groupKeys.members(groupId) })
  });
}
function useRemoveMember(groupId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId) => unwrap(
      api().DELETE("/v1/groups/{groupId}/members/{userId}", {
        params: { path: { groupId, userId } }
      })
    ),
    onSuccess: () => qc.invalidateQueries({ queryKey: groupKeys.all })
  });
}
function useCreateInvitation(groupId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => unwrap(
      api().POST("/v1/groups/{groupId}/invitations", { params: { path: { groupId } }, body })
    ),
    onSuccess: () => qc.invalidateQueries({ queryKey: groupKeys.invitations(groupId) })
  });
}
function useRevokeInvitation(groupId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invitationId) => unwrap(
      api().POST("/v1/invitations/{invitationId}/revoke", { params: { path: { invitationId } } })
    ),
    onSuccess: () => qc.invalidateQueries({ queryKey: groupKeys.invitations(groupId) })
  });
}

// src/goals.ts
var goalKeys = {
  all: ["goals"],
  forGroup: (groupId) => [...goalKeys.all, "group", groupId],
  detail: (goalId) => [...goalKeys.all, goalId]
};
function useGroupGoals(groupId) {
  return useQuery2({
    queryKey: goalKeys.forGroup(groupId),
    queryFn: () => unwrap2(api().GET("/v1/groups/{groupId}/goals", { params: { path: { groupId } } }))
  });
}
function useGoal(goalId) {
  return useQuery2({
    queryKey: goalKeys.detail(goalId),
    queryFn: () => unwrap2(api().GET("/v1/goals/{goalId}", { params: { path: { goalId } } }))
  });
}
function useCreateGoal(groupId) {
  const qc = useQueryClient2();
  return useMutation2({
    mutationFn: (body) => unwrap2(api().POST("/v1/groups/{groupId}/goals", { params: { path: { groupId } }, body })),
    onSuccess: (goal) => {
      qc.setQueryData(goalKeys.detail(goal.id), goal);
      void qc.invalidateQueries({ queryKey: goalKeys.forGroup(groupId) });
      void qc.invalidateQueries({ queryKey: groupKeys.activity(groupId) });
    }
  });
}
function useUpdateGoal(goalId, groupId) {
  const qc = useQueryClient2();
  return useMutation2({
    mutationFn: (body) => unwrap2(api().PATCH("/v1/goals/{goalId}", { params: { path: { goalId } }, body })),
    onSuccess: (goal) => {
      qc.setQueryData(goalKeys.detail(goalId), goal);
      void qc.invalidateQueries({ queryKey: goalKeys.forGroup(groupId) });
    }
  });
}

// src/contributions.ts
var contributionKeys = {
  history: (goalId) => ["contributions", goalId],
  attempt: (attemptId) => ["payment-attempts", attemptId]
};
function useContributionHistory(goalId) {
  return useInfiniteQuery2({
    queryKey: contributionKeys.history(goalId),
    initialPageParam: void 0,
    queryFn: ({ pageParam }) => unwrap3(
      api().GET("/v1/goals/{goalId}/contributions", {
        params: { path: { goalId }, query: { limit: 20, cursor: pageParam } }
      })
    ),
    getNextPageParam: (last) => last.nextCursor ?? void 0
  });
}
function useStartContribution(goalId) {
  return useMutation3({
    mutationFn: ({ amount, idempotencyKey }) => unwrap3(
      api().POST("/v1/goals/{goalId}/contributions", {
        params: { path: { goalId }, header: { "Idempotency-Key": idempotencyKey } },
        body: { amount }
      })
    )
  });
}
function usePaymentAttempt(attemptId) {
  return useQuery3({
    queryKey: contributionKeys.attempt(attemptId),
    queryFn: () => unwrap3(api().GET("/v1/payment-attempts/{attemptId}", { params: { path: { attemptId } } })),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status && TERMINAL_PAYMENT_STATUSES.includes(status)) return false;
      const n = query.state.dataUpdateCount;
      return n < 5 ? 2e3 : n < 15 ? 3e3 : 5e3;
    },
    refetchIntervalInBackground: false
  });
}
function useVerifyAttempt(attemptId) {
  return useMutation3({
    mutationFn: () => unwrap3(
      api().POST("/v1/payment-attempts/{attemptId}/verify", { params: { path: { attemptId } } })
    )
  });
}
function usePaymentStatus(attemptId, slowAfterMs = 2 * 6e4) {
  const qc = useQueryClient3();
  const attempt = usePaymentAttempt(attemptId);
  const verify = useVerifyAttempt(attemptId);
  const verified = useRef(false);
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (verified.current) return;
    verified.current = true;
    verify.mutate(void 0, {
      onSuccess: (data2) => qc.setQueryData(contributionKeys.attempt(attemptId), data2)
    });
  }, [attemptId, qc, verify]);
  useEffect(() => {
    const t = setTimeout(() => setSlow(true), slowAfterMs);
    return () => clearTimeout(t);
  }, [attemptId, slowAfterMs]);
  const data = attempt.data;
  useEffect(() => {
    if (data?.status !== "successful") return;
    void qc.invalidateQueries({ queryKey: goalKeys.detail(data.goalId) });
    void qc.invalidateQueries({ queryKey: goalKeys.forGroup(data.groupId) });
    void qc.invalidateQueries({ queryKey: contributionKeys.history(data.goalId) });
    void qc.invalidateQueries({ queryKey: groupKeys.activity(data.groupId) });
  }, [data?.status, data?.goalId, data?.groupId, qc]);
  return { attempt, slow };
}

// src/invitations.ts
import { unwrap as unwrap4 } from "@chipn/api-client";
import { useMutation as useMutation4, useQuery as useQuery4, useQueryClient as useQueryClient4 } from "@tanstack/react-query";
var invitationKeys = {
  preview: (code) => ["invitation", code]
};
function useInvitationPreview(code) {
  return useQuery4({
    queryKey: invitationKeys.preview(code),
    queryFn: () => unwrap4(api().GET("/v1/invitations/code/{code}", { params: { path: { code } } }))
  });
}
function useAcceptInvitation(code) {
  const qc = useQueryClient4();
  return useMutation4({
    mutationFn: () => unwrap4(api().POST("/v1/invitations/code/{code}/accept", { params: { path: { code } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: groupKeys.all })
  });
}

// src/users.ts
import { unwrap as unwrap5 } from "@chipn/api-client";
import { useMutation as useMutation5, useQuery as useQuery5, useQueryClient as useQueryClient5 } from "@tanstack/react-query";
var meKey = ["me"];
function useMe() {
  return useQuery5({
    queryKey: meKey,
    queryFn: () => unwrap5(api().GET("/v1/me")),
    staleTime: 5 * 6e4
  });
}
function useUpdateMe() {
  const qc = useQueryClient5();
  return useMutation5({
    mutationFn: (body) => unwrap5(api().PATCH("/v1/me", { body })),
    onSuccess: (user) => qc.setQueryData(meKey, user)
  });
}
export {
  configureQueries,
  contributionKeys,
  goalKeys,
  groupKeys,
  invitationKeys,
  meKey,
  useAcceptInvitation,
  useActivity,
  useArchiveGroup,
  useChangeRole,
  useContributionHistory,
  useCreateGoal,
  useCreateGroup,
  useCreateInvitation,
  useGoal,
  useGroup,
  useGroupGoals,
  useInvitationPreview,
  useInvitations,
  useMe,
  useMembers,
  useMyGroups,
  usePaymentAttempt,
  usePaymentStatus,
  useRemoveMember,
  useRevokeInvitation,
  useStartContribution,
  useUpdateGoal,
  useUpdateGroup,
  useUpdateMe,
  useVerifyAttempt
};
